import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {formatDistanceToNow} from 'date-fns';
import {MessageSquareWarning, Search, Sparkles} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Tabs,
  TabsList,
  TabsTrigger,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {
  useClassificationConfig,
  useRejectedSubmissionCounts,
  useRejectedSubmissions,
  useReviewFeedbackSummaries
} from '@/api/hooks';
import {type RejectionSource} from '@/api/rejectedSubmissions';
import {RejectedSubmissionsTable} from '@/components/rejectedSubmissions/RejectedSubmissionsTable';
import {TablePanel} from '@/components/ui/table-panel';
import {isPolicyViolationType} from '@/lib/classificationType';
import {useDebounce} from '@/lib/useDebounce';

type SourceTab = 'all' | RejectionSource;

const SOURCE_TABS: Array<{value: SourceTab; label: string}> = [
  {value: 'all', label: 'All'},
  {value: 'qc', label: 'QC rejections'},
  {value: 'qa', label: 'QA corrections'}
];

const PAGE_SIZE = 25;

const isSourceTab = (value: string | null): value is SourceTab =>
  value === 'all' || value === 'qc' || value === 'qa';

export function RejectedSubmissionsPage() {
  // Every filter and the page live in the URL, so a reload or a shared link lands on the same list.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSource = searchParams.get('tab');
  const source: SourceTab = isSourceTab(rawSource) ? rawSource : 'all';
  const policyViolation = searchParams.get('vertical') ?? '';
  const q = searchParams.get('q') ?? '';
  const dateFrom = searchParams.get('from') ?? '';
  const dateTo = searchParams.get('to') ?? '';
  const page = Math.max(Number(searchParams.get('page')) || 1, 1);

  const [search, setSearch] = useState(q);
  const debouncedSearch = useDebounce(search.trim(), 300);

  // Writes a change into the URL without a history entry. Any filter change starts back at the
  // first page; only paging itself keeps it.
  function update(change: (next: URLSearchParams) => void, keepPage = false) {
    const next = new URLSearchParams(searchParams);
    change(next);
    if (!keepPage) {
      next.delete('page');
    }

    setSearchParams(next, {replace: true});
  }

  function setParam(key: string, value: string) {
    update((next) => {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    });
  }

  function setPage(value: number) {
    update((next) => {
      if (value > 1) {
        next.set('page', String(value));
      } else {
        next.delete('page');
      }
    }, true);
  }

  // Follow a q changed from outside (browser back, a link). Our own write always lands as
  // q === debouncedSearch, so skipping that case never clobbers what the analyst is still typing.
  useEffect(() => {
    if (q !== debouncedSearch) {
      setSearch(q);
    }
    // Only an external q change should resync; debouncedSearch is read, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    if (debouncedSearch !== q) {
      setParam('q', debouncedSearch);
    }
    // Only a settled search should write the URL; setParam closes over the current params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const classifications = useClassificationConfig();
  const verticals = (classifications.data ?? [])
    .filter((c) => isPolicyViolationType(c.type))
    .map((c) => c.name);

  const invalidRange = dateFrom !== '' && dateTo !== '' && dateTo < dateFrom;

  const filters = {
    ...(source === 'all' ? {} : {source: [source]}),
    // The date inputs are local calendar days; the range is inclusive of the whole "to" day.
    ...(dateFrom ? {date_from: new Date(`${dateFrom}T00:00:00`).toISOString()} : {}),
    ...(dateTo ? {date_to: new Date(`${dateTo}T23:59:59.999`).toISOString()} : {}),
    ...(q ? {q} : {})
  };
  const {data, isLoading} = useRejectedSubmissions({
    ...filters,
    ...(policyViolation ? {policy_violation: policyViolation} : {}),
    page,
    page_size: PAGE_SIZE
  });
  const counts = new Map(
    (useRejectedSubmissionCounts(filters).data ?? []).map((c) => [c.policy_violation, c.count])
  );

  const total = data?.total_count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="text-foreground flex h-full w-full flex-col">
      <div className="bg-card border-border shrink-0 space-y-3 border-b px-5 py-3">
        <p className="text-muted-foreground text-xs">
          Your own reviewer feedback: submissions QC rejected before delivery and ones Weekly QA
          corrected after it. Only you can see this list.
        </p>

        <ToggleGroup
          variant="outline"
          spacing={0}
          size="sm"
          aria-label="Vertical"
          className="flex-wrap"
          value={policyViolation ? [policyViolation] : []}
          onValueChange={(next) => {
            // Deselecting the active vertical goes back to every vertical.
            setParam('vertical', next[0] ?? '');
          }}>
          {verticals.map((vertical) => (
            <ToggleGroupItem key={vertical} value={vertical}>
              {vertical}
              <Badge variant={policyViolation === vertical ? 'default' : 'secondary'}>
                {counts.get(vertical) ?? 0}
              </Badge>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <Tabs
          value={source}
          onValueChange={(value) => {
            setParam('tab', value === 'all' ? '' : value);
          }}>
          <TabsList>
            {SOURCE_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-end gap-2">
          <Field className="w-auto">
            <FieldLabel htmlFor="rejected-search">Search</FieldLabel>
            <InputGroup className="w-56 max-w-full">
              <InputGroupAddon>
                <Search />
              </InputGroupAddon>
              <InputGroupInput
                id="rejected-search"
                type="search"
                placeholder="Title, content ID or notes"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
            </InputGroup>
          </Field>
          <Field className="w-auto">
            <FieldLabel htmlFor="rejected-from">From</FieldLabel>
            <Input
              id="rejected-from"
              type="date"
              value={dateFrom}
              className="w-40"
              onChange={(event) => {
                setParam('from', event.target.value);
              }}
            />
          </Field>
          <Field className="w-auto">
            <FieldLabel htmlFor="rejected-to">To</FieldLabel>
            <Input
              id="rejected-to"
              type="date"
              value={dateTo}
              className="w-40"
              onChange={(event) => {
                setParam('to', event.target.value);
              }}
            />
          </Field>
        </div>

        {invalidRange ? (
          <p role="alert" className="text-destructive-ink text-xs">
            The To date is before the From date, so nothing can match.
          </p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <TeamLearningPanel vertical={policyViolation} />

        <TablePanel>
          <RejectedSubmissionsTable items={data?.results ?? []} isLoading={isLoading} />
        </TablePanel>

        {total > PAGE_SIZE ? (
          <div className="flex items-center justify-end gap-2 text-xs">
            <span className="text-muted-foreground">
              Page {page} of {pageCount} · {total} total
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => {
                setPage(page - 1);
              }}>
              Previous
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={page >= pageCount}
              onClick={() => {
                setPage(page + 1);
              }}>
              Next
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TeamLearningPanel({vertical}: Readonly<{vertical: string}>) {
  const {data: summaries = [], isSuccess} = useReviewFeedbackSummaries();
  const active = summaries.find((s) => s.policy_violation === vertical);

  // Until the summaries have loaded (or if the request failed) "no summary" would be a false claim.
  if (!vertical || !isSuccess) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      {active?.summary ? (
        <Alert>
          <Sparkles />
          <AlertTitle>{active.policy_violation} team summary</AlertTitle>
          <AlertDescription className="grid gap-1.5">
            <p className="text-foreground">{active.summary.summary}</p>
            <p>
              Based on {active.qc_count} QC {active.qc_count === 1 ? 'rejection' : 'rejections'} +{' '}
              {active.qa_count} QA {active.qa_count === 1 ? 'correction' : 'corrections'} · updated{' '}
              {formatDistanceToNow(new Date(active.generated_at), {addSuffix: true})}
            </p>
            <p>AI-generated from anonymised feedback across your vertical.</p>
          </AlertDescription>
        </Alert>
      ) : (
        <Empty variant="outline" className="py-8">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <MessageSquareWarning />
            </EmptyMedia>
            <EmptyTitle>No team summary yet for {vertical}</EmptyTitle>
            <EmptyDescription>
              A summary appears once a vertical has enough QC and QA feedback to learn from.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </section>
  );
}
